/**
 * Service Items API - Request-Service Tasks (Items)
 *
 * Client-side API layer for managing service items on jobs.
 * Server API uses "tasks" terminology; UI uses "items".
 *
 * Items represent material assignments or workflow steps on a service:
 * - material: product assigned to a section/area (e.g., "Windshield" → Xpel XR Plus)
 * - work: operational step (Phase 2)
 */

import { HTTPService } from './httpService';

const API = new HTTPService();

// ============================================================================
// Request-Service Items (Job-Level)
// ============================================================================

/**
 * Get all items for a request service
 * @param {number} requestServiceId - The request_service row PK
 * @returns {Promise<Array>} List of items
 */
export const getItems = async (requestServiceId, { silent = false } = {}) => {
  try {
    const { data } = await API.get(`/request-service-tasks/${requestServiceId}`, {}, { silent });
    return data.data || data;
  } catch (error) {
    if (silent) return [];
    throw error;
  }
};

/**
 * Create a new item on a request service
 * @param {number} requestServiceId - The request_service row PK
 * @param {Object} itemData
 * @param {string} itemData.label - Item label (e.g., "Windshield", "Front Doors")
 * @param {string} [itemData.type='material'] - Item type: 'material' | 'work'
 * @param {number} [itemData.recommended_product_id] - Recommended product ID
 * @param {string} [itemData.recommended_product_name] - Recommended product name
 * @param {string} [itemData.recommended_product_brand] - Recommended product brand
 * @param {string} [itemData.recommended_product_unit] - Recommended product unit
 * @param {number} [itemData.expected_qty] - Expected quantity
 * @returns {Promise<Object>} Created item
 */
export const createItem = async (requestServiceId, itemData) => {
  const { data } = await API.post(`/request-service-tasks/${requestServiceId}`, itemData);
  return data.data || data;
};

/**
 * Update an existing item
 * @param {number} taskId - The item's primary key
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated item
 */
export const updateItem = async (taskId, updates) => {
  const { data } = await API.patch(`/request-service-tasks/task/${taskId}`, updates);
  return data.data || data;
};

/**
 * Mark an item as complete
 * @param {number} taskId - The item's primary key
 * @returns {Promise<Object>} Result
 */
export const completeItem = async (taskId) => {
  const { data } = await API.post(`/request-service-tasks/task/${taskId}/complete`);
  return data.data || data;
};

/**
 * Complete an item with optional material data
 * @param {number} taskId - The item's primary key
 * @param {Object} [materialData] - Optional material capture data
 * @returns {Promise<Object>} Result
 */
export const completeItemWithMaterials = async (taskId, materialData = null) => {
  const body = materialData || {};
  const { data } = await API.post(`/request-service-tasks/task/${taskId}/complete`, body);
  return data.data || data;
};

/**
 * Delete an item
 * @param {number} taskId - The item's primary key
 * @returns {Promise<Object>} Result
 */
export const deleteItem = async (taskId) => {
  const { data } = await API.delete(`/request-service-tasks/task/${taskId}`);
  return data.data || data;
};

/**
 * Bulk assign all items on a request service to a technician.
 * @param {number} requestServiceId - The request_service row PK
 * @param {number} technicianId - User to assign
 * @param {Object} [options]
 * @param {'fill'|'overwrite'} [options.mode='fill'] - 'fill' only assigns NULL tasks; 'overwrite' replaces all and updates the service-level lead tech.
 * @returns {Promise<Object>} { ok, tech_assignment, cascade_result }
 */
export const bulkAssign = async (requestServiceId, technicianId, { mode = 'fill' } = {}) => {
  const { data } = await API.post(`/request-service-tasks/${requestServiceId}/bulk-assign`, {
    technician_id: technicianId,
    mode,
  });
  return data.data || data;
};

/**
 * Get all items assigned to a technician
 * @param {number} userId - The technician's user ID
 * @returns {Promise<Array>} List of items
 */
export const getItemsByTechnician = async (userId) => {
  const { data } = await API.get(`/request-service-tasks/technician/${userId}`);
  return data.data || data;
};

// Default export for convenience
const serviceItemsService = {
  getItems,
  createItem,
  updateItem,
  completeItem,
  completeItemWithMaterials,
  deleteItem,
  bulkAssign,
  getItemsByTechnician
};

export default serviceItemsService;
